import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import Chip from '@/components/ui/Chip';
import Botao from '@/components/ui/Botao';
import Dica from '@/components/feature/Dica';
import DicaPrimeiraVez from '@/components/feature/DicaPrimeiraVez';
import { useModoEquipe } from '@/components/feature/ModoEquipe';
import { tocarBipes, vibrar } from '@/pages/portaria/utils';
import { formatarCentavos } from '@/pages/loja/utils';
import NumeroAnimado from '@/components/movimento/NumeroAnimado';
import GradeProdutos from './components/GradeProdutos';
import PainelPedido from './components/PainelPedido';
import FolhaDinheiro from './components/FolhaDinheiro';
import FolhaQuantidade from './components/FolhaQuantidade';
import ModalRecentes from './components/ModalRecentes';
import TelaCartao from './components/TelaCartao';
import TelaSucesso from './components/TelaSucesso';
import FolhaEscolherCaixa from './components/FolhaEscolherCaixa';
import FolhaReimprimir from './components/FolhaReimprimir';
import ModalDesfazer from './components/ModalDesfazer';
import BarraUltimaVenda from './components/BarraUltimaVenda';
import { CartaoAbrirCaixa, FaixaCaixaAberto } from './components/TurnoCaixa';
import ModalSangria from './components/ModalSangria';
import { normalizarTurno } from './turno';
import { TelaRecusa } from './components/TelasFluxo';
import type {
  CaixaMeuEvento,
  CaixaOpcao,
  CaixaProdutos,
  CaixaTurno,
  EstadoImpressao,
  ImpressaoPedido,
  LinhaPedido,
  MaquininhaTipo,
  MeioRecebimento,
  MetodoCaixa,
  ProdutoCaixa,
  ResultadoImpressao,
  ResultadoImpressaoItem,
  SituacaoCaixa,
  SituacaoPedidoCaixa,
  UltimaVenda,
  VendaRecente,
  VendaRegistrada,
} from './types';

type Estado = 'carregando' | 'escolher-evento' | 'pronto' | 'erro' | 'sem-evento';

interface EventoCandidato {
  id: number;
  nome: string;
}

interface CartaoEmCurso {
  pedidoId: number;
  numero: number;
  totalCentavos: number;
  tipo: MaquininhaTipo | null;
}

const IMPRESSAO_VAZIA: EstadoImpressao = {
  imprimindo: false,
  resultado: null,
  itens: [],
  total: 0,
};

const ROTULO_METODO: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'Pix',
  cartao: 'Cartão',
  debito: 'Débito',
  credito: 'Crédito',
};

export default function CaixaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, pronto } = useAuth();
  const { verificado: equipeVerificado, ativo: modoEquipe, dados: dadosEquipe } = useModoEquipe();
  const telaCaixa = useMemo(
    () => (dadosEquipe?.telas ?? []).find((t) => t.tela === 'caixa') ?? null,
    [dadosEquipe],
  );

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [candidatos, setCandidatos] = useState<EventoCandidato[]>([]);
  const [eventoId, setEventoId] = useState<number | null>(null);
  const [caixa, setCaixa] = useState<CaixaProdutos | null>(null);
  const [caixas, setCaixas] = useState<CaixaOpcao[]>([]);
  const [caixaEscolhido, setCaixaEscolhido] = useState<CaixaOpcao | null>(null);
  const [escolhendoCaixa, setEscolhendoCaixa] = useState(false);
  // Caixa que a direção definiu para esta pessoa (conta pessoal, fora do modo equipe).
  const [caixaDaDirecao, setCaixaDaDirecao] = useState<number | null>(null);
  const [quantidades, setQuantidades] = useState<Map<number, number>>(new Map());
  const [relogio, setRelogio] = useState('');

  const [folhaDinheiro, setFolhaDinheiro] = useState(false);
  const [quantidadeAlvo, setQuantidadeAlvo] = useState<ProdutoCaixa | null>(null);
  const [confirmandoMetodo, setConfirmandoMetodo] = useState<MetodoCaixa | null>(null);
  const [registrando, setRegistrando] = useState(false);
  const [maquininha, setMaquininha] = useState<CartaoEmCurso | null>(null);
  const [venda, setVenda] = useState<VendaRegistrada | null>(null);
  const [vendaMaquininha, setVendaMaquininha] = useState(false);
  const [impressao, setImpressao] = useState<EstadoImpressao>(IMPRESSAO_VAZIA);
  const [recusa, setRecusa] = useState<string | null>(null);
  const [recentes, setRecentes] = useState(false);

  // Turno de caixa (abrir/fechar). Se a chamada falhar, nada é mostrado.
  const [turno, setTurno] = useState<CaixaTurno | null>(null);
  const [turnoOk, setTurnoOk] = useState(false);
  const [turnoDispensado, setTurnoDispensado] = useState(false);
  const [sangriaAberta, setSangriaAberta] = useState(false);

  const [ultimaVenda, setUltimaVenda] = useState<UltimaVenda | null>(null);
  const [barraVisivel, setBarraVisivel] = useState(false);
  const [barraReimprimir, setBarraReimprimir] = useState<number | null>(null);
  const [barraDesfazer, setBarraDesfazer] = useState<number | null>(null);
  const [pendente, setPendente] = useState<VendaRecente | null>(null);

  const processandoRef = useRef(false);
  const escolhaInicialRef = useRef(false);
  const maquininhaRef = useRef(false);
  // Acompanhamento da impressão em segundo plano (ficha "a caminho").
  const ultimaVendaRef = useRef<UltimaVenda | null>(null);
  const barraVisivelRef = useRef(false);
  const acompanhamentosRef = useRef<Set<ReturnType<typeof setInterval>>>(new Set());

  // Mantém os refs em sincronia para o acompanhamento ler o estado mais recente.
  useEffect(() => {
    ultimaVendaRef.current = ultimaVenda;
  }, [ultimaVenda]);
  useEffect(() => {
    barraVisivelRef.current = barraVisivel;
  }, [barraVisivel]);

  // Limpa os acompanhamentos ao desmontar.
  useEffect(() => {
    const timers = acompanhamentosRef.current;
    return () => {
      timers.forEach((t) => clearInterval(t));
      timers.clear();
    };
  }, []);

  // Relógio no cabeçalho.
  useEffect(() => {
    const t = setInterval(() => setRelogio(new Date().toLocaleTimeString('pt-BR')), 1000);
    return () => clearInterval(t);
  }, []);

  // Mantém a tela acesa enquanto o operador está no caixa.
  useEffect(() => {
    let lock: { release?: () => Promise<void> } | null = null;
    async function adquirir() {
      try {
        const wl = navigator as Navigator & {
          wakeLock?: { request: (t: string) => Promise<{ release?: () => Promise<void> }> };
        };
        lock = await wl.wakeLock?.request('screen');
      } catch {
        /* sem suporte */
      }
    }
    adquirir();
    const aoVisivel = () => {
      if (document.visibilityState === 'visible') adquirir();
    };
    document.addEventListener('visibilitychange', aoVisivel);
    return () => {
      document.removeEventListener('visibilitychange', aoVisivel);
      lock?.release?.();
    };
  }, []);

  // Entrada: logado + descobre os eventos onde pode operar o caixa.
  useEffect(() => {
    if (!equipeVerificado) return;
    if (!pronto) return;
    if (!user) {
      navigate(`/entrar?voltar=${encodeURIComponent('/caixa')}`, { replace: true });
      return;
    }
    let ativo = true;
    (async () => {
      setEstado('carregando');
      setErro(null);
      try {
        const { data: caixas } = await supabase.rpc('caixa_meus_eventos');
        const lista: EventoCandidato[] = ((caixas as CaixaMeuEvento[]) || []).map((c) => ({
          id: c.evento_id,
          nome: c.evento || `Evento ${c.evento_id}`,
        }));

        if (!ativo) return;
        lista.sort((a, b) => a.nome.localeCompare(b.nome));
        setCandidatos(lista);

        if (lista.length === 0) {
          setEstado('sem-evento');
          return;
        }
        if (lista.length === 1) {
          abrirEvento(lista[0].id);
          return;
        }
        setEstado('escolher-evento');
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e));
          setEstado('erro');
        }
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, equipeVerificado, pronto, user]);

  function abrirEvento(id: number) {
    localStorage.setItem('caixa:evento', String(id));
    setEventoId(id);
    void carregarCaixa(id);
  }

  function escolherCaixaInicial(lista: CaixaOpcao[], idEvento: number, definido: number | null) {
    if (lista.length === 0) {
      setCaixaEscolhido(null);
      return;
    }
    // Quem define em qual caixa a pessoa serve é o organizador: se a direção já
    // escolheu o caixa desta conta, usa sempre ele (ignora localStorage e ?caixa=).
    if (definido != null) {
      const fixo = lista.find((c) => c.caixa === definido);
      if (fixo) {
        setCaixaEscolhido(fixo);
        return;
      }
    }
    if (modoEquipe) {
      // Modo equipe: o caixa é o que a direção escolheu (com a maquininha dele).
      const alvo = telaCaixa?.caixa_id;
      if (alvo != null) {
        const fixo = lista.find((c) => c.caixa === alvo);
        if (fixo) {
          setCaixaEscolhido(fixo);
          return;
        }
      }
    } else {
      // Fora do modo equipe: um ?caixa=ID na URL manda (e fica salvo no aparelho).
      const daUrl = Number(searchParams.get('caixa'));
      if (Number.isFinite(daUrl) && daUrl > 0) {
        const achadoUrl = lista.find((c) => c.caixa === daUrl);
        if (achadoUrl) {
          localStorage.setItem(`seliga_caixa_${idEvento}`, String(achadoUrl.caixa));
          setCaixaEscolhido(achadoUrl);
          return;
        }
      }
    }
    if (lista.length === 1) {
      setCaixaEscolhido(lista[0]);
      return;
    }
    const salvo = Number(localStorage.getItem(`seliga_caixa_${idEvento}`));
    const achado = lista.find((c) => c.caixa === salvo);
    if (achado) {
      setCaixaEscolhido(achado);
      return;
    }
    setCaixaEscolhido(null);
    setEscolhendoCaixa(true);
  }

  async function carregarCaixa(id: number) {
    try {
      const { data, error } = await supabase.rpc('caixa_produtos', { p_evento: id });
      if (error) throw error;
      const dados = (data as CaixaProdutos) ?? null;
      setCaixa(dados);
      const lista = (dados?.caixas ?? []) as CaixaOpcao[];
      setCaixas(lista);

      // Fora do modo equipe: a direção pode ter definido o caixa desta pessoa.
      // (No modo equipe o caixa já vem da tela, não se consulta aqui.)
      let definido: number | null = null;
      if (!modoEquipe) {
        try {
          const { data: meu } = await supabase.rpc('caixa_meu_caixa', { p_evento: id });
          const alvo = (meu as { caixa_id?: number } | null)?.caixa_id;
          if (alvo != null && lista.some((c) => c.caixa === alvo)) definido = alvo;
        } catch {
          /* segue exatamente como hoje */
        }
      }
      setCaixaDaDirecao(definido);

      if (!escolhaInicialRef.current) {
        escolhaInicialRef.current = true;
        escolherCaixaInicial(lista, id, definido);
      }
      setEstado('pronto');
      void verificarPendente(id, dados?.operador ?? '');
      void carregarTurno(id);
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setEstado('erro');
    }
  }

  // Lê o turno aberto (se houver). Qualquer erro esconde o bloco novo.
  const carregarTurno = useCallback(async (id: number) => {
    try {
      const { data, error } = await supabase.rpc('caixa_turno_atual', { p_evento: id });
      if (error) throw error;
      setTurno(normalizarTurno(data));
      setTurnoOk(true);
    } catch {
      setTurno(null);
      setTurnoOk(false);
    }
  }, []);

  // Abre o caixa com o troco informado. Devolve sucesso/erro para o cartão.
  async function abrirCaixaTurno(trocoCentavos: number): Promise<{ ok: boolean; erro?: string }> {
    if (eventoId == null) return { ok: false, erro: 'Evento não identificado.' };
    try {
      const { data, error } = await supabase.rpc('caixa_turno_abrir', {
        p_evento: eventoId,
        p_troco_centavos: trocoCentavos,
        p_caixa: caixaEscolhido?.caixa ?? null,
      });
      if (error) throw error;
      const t = normalizarTurno(data);
      if (t) setTurno(t);
      else void carregarTurno(eventoId);
      setTurnoOk(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, erro: mensagemAmigavel(e) };
    }
  }

  // Venda que ficou "aguardando a maquininha" (o celular recarregou no meio).
  async function verificarPendente(id: number, operador: string) {
    // Não interrompe uma cobrança em andamento nem reabre o cartão em cima dela.
    if (maquininhaRef.current) return;
    try {
      const { data, error } = await supabase.rpc('caixa_vendas_recentes', {
        p_evento: id,
        p_limite: 20,
      });
      if (error) return;
      const lista = (data as VendaRecente[]) || [];
      const achado = lista.find(
        (v) => v.status === 'aguardando' && (!operador || v.operador === operador),
      );
      if (achado) setPendente(achado);
    } catch {
      /* silencioso: não trava a tela por causa disso */
    }
  }

  function escolherCaixa(c: CaixaOpcao) {
    if (eventoId != null) localStorage.setItem(`seliga_caixa_${eventoId}`, String(c.caixa));
    setCaixaEscolhido(c);
    setEscolhendoCaixa(false);
  }

  useEffect(() => {
    maquininhaRef.current = maquininha != null;
  }, [maquininha]);

  // Tempo real: sinal da vitrine reatualiza o estoque sem recarregar a tela.
  useEffect(() => {
    if (eventoId == null) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const canal = supabase
      .channel(`caixa-${eventoId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'caixa_sinais',
          filter: `evento_id=eq.${eventoId},origem=eq.vitrine`,
        },
        () => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => carregarCaixa(eventoId), 500);
        },
      )
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(canal);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  const linhas: LinhaPedido[] = useMemo(() => {
    if (!caixa) return [];
    const mapa = new Map<number, ProdutoCaixa & { pontoNome: string }>();
    caixa.pontos.forEach((ponto) => {
      ponto.produtos.forEach((p) => mapa.set(p.produto_id, { ...p, pontoNome: ponto.nome }));
    });
    const resultado: LinhaPedido[] = [];
    quantidades.forEach((qtd, produtoId) => {
      const p = mapa.get(produtoId);
      if (p && qtd > 0) resultado.push({ produto: p, pontoNome: p.pontoNome, quantidade: qtd });
    });
    return resultado;
  }, [caixa, quantidades]);

  const total = useMemo(
    () => linhas.reduce((soma, l) => soma + l.produto.preco_centavos * l.quantidade, 0),
    [linhas],
  );

  // Barra "última venda": aparece após fechar uma venda, some ao tocar em produtos ou em 60 s.
  // Quando a ficha não saiu (fichaPendente), a barra NÃO some sozinha nem ao tocar em produtos.
  useEffect(() => {
    if (!ultimaVenda) return;
    setBarraVisivel(true);
    if (ultimaVenda.fichaPendente) return;
    const t = setTimeout(() => setBarraVisivel(false), 60000);
    return () => clearTimeout(t);
  }, [ultimaVenda]);

  useEffect(() => {
    if (total > 0 && !ultimaVenda?.fichaPendente) setBarraVisivel(false);
  }, [total, ultimaVenda]);

  function aoAdicionar(p: ProdutoCaixa) {
    if (p.esgotado) return;
    setQuantidades((m) => {
      const atual = m.get(p.produto_id) ?? 0;
      let prox = atual + 1;
      if (p.max_por_pedido && prox > p.max_por_pedido) prox = p.max_por_pedido;
      if (p.disponivel != null && prox > p.disponivel) prox = p.disponivel;
      const novo = new Map(m);
      if (prox <= 0) novo.delete(p.produto_id);
      else novo.set(p.produto_id, prox);
      return novo;
    });
  }

  function aoRemover(p: ProdutoCaixa) {
    setQuantidades((m) => {
      const atual = m.get(p.produto_id) ?? 0;
      const prox = atual - 1;
      const novo = new Map(m);
      if (prox <= 0) novo.delete(p.produto_id);
      else novo.set(p.produto_id, prox);
      return novo;
    });
  }

  function definirQuantidade(produtoId: number, qtd: number) {
    setQuantidades((m) => {
      const novo = new Map(m);
      if (qtd <= 0) novo.delete(produtoId);
      else novo.set(produtoId, qtd);
      return novo;
    });
  }

  function aoLimpar() {
    setQuantidades(new Map());
  }

  // O que cada botão de receber faz.
  function aoPagar(meio: MeioRecebimento) {
    if (processandoRef.current || total === 0) return;
    const comMaquininha = caixaEscolhido?.cartao_na_maquininha === true;
    if (meio === 'dinheiro') {
      setFolhaDinheiro(true);
      return;
    }
    if (comMaquininha && (meio === 'pix' || meio === 'debito' || meio === 'credito')) {
      void iniciarMaquininha(meio);
      return;
    }
    if (meio === 'pix' || meio === 'cartao') setConfirmandoMetodo(meio);
  }

  async function registrarVenda(metodo: MetodoCaixa, recebido: number | null) {
    if (processandoRef.current || eventoId == null) return;
    processandoRef.current = true;
    setRegistrando(true);
    setConfirmandoMetodo(null);
    setFolhaDinheiro(false);
    setErro(null);

    const itens = linhas.map((l) => ({ produto_id: l.produto.produto_id, quantidade: l.quantidade }));

    try {
      const { data, error } = await supabase.rpc('caixa_registrar_venda', {
        p_evento: eventoId,
        p_itens: itens,
        p_metodo: metodo,
        p_recebido_centavos: metodo === 'dinheiro' ? recebido : null,
        p_observacao: null,
        p_caixa: caixaEscolhido?.caixa ?? null,
      });
      if (error) throw error;

      const v = data as VendaRegistrada;
      if (!v || v.ok === false) {
        setRecusa('Não foi possível registrar a venda. Tente de novo.');
        return;
      }

      setVenda({ ...v, fichas: v.fichas ?? [] });
      setVendaMaquininha(false);
      setImpressao(IMPRESSAO_VAZIA);
      setQuantidades(new Map());
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) vibrar(100);
      tocarBipes(1, 880);

      if (v.imprimir) void imprimirFichas(v.pedido_id, false);
    } catch (e) {
      setRecusa(mensagemAmigavel(e));
    } finally {
      processandoRef.current = false;
      setRegistrando(false);
    }
  }

  // Pix/Débito/Crédito na maquininha: reserva o estoque e manda a cobrança.
  async function iniciarMaquininha(tipo: MaquininhaTipo) {
    if (processandoRef.current || eventoId == null || !caixaEscolhido) return;
    processandoRef.current = true;
    setRegistrando(true);
    setErro(null);

    const itens = linhas.map((l) => ({ produto_id: l.produto.produto_id, quantidade: l.quantidade }));
    const totalAtual = total;

    try {
      const { data, error } = await supabase.rpc('caixa_iniciar_cartao', {
        p_evento: eventoId,
        p_itens: itens,
        p_caixa: caixaEscolhido.caixa,
      });
      if (error) throw error;

      const pedido = data as { pedido_id?: number; numero?: number; total_centavos?: number };
      if (!pedido?.pedido_id) {
        setRecusa('Não foi possível iniciar a cobrança. Tente de novo.');
        return;
      }

      const { data: cob, error: errCob } = await supabase.functions.invoke('caixa-cobrar', {
        body: { acao: 'iniciar', pedido_id: pedido.pedido_id, tipo },
      });
      if (errCob) {
        setRecusa('Sem conexão agora. Tente de novo.');
        return;
      }
      if (!cob?.ok) {
        setRecusa(cob?.erro ?? 'Não foi possível enviar para a maquininha.');
        return;
      }

      setMaquininha({
        pedidoId: pedido.pedido_id,
        numero: pedido.numero ?? 0,
        totalCentavos: pedido.total_centavos ?? totalAtual,
        tipo,
      });
    } catch (e) {
      setRecusa(mensagemAmigavel(e));
    } finally {
      processandoRef.current = false;
      setRegistrando(false);
    }
  }

  // Pagamento na maquininha confirmado pelo servidor (nunca pelo toque).
  function concluirVendaMaquininha(
    situacao: SituacaoCaixa,
    base: { pedidoId: number; numero: number; totalCentavos: number },
  ) {
    const meio = situacao.meio;
    const metodo: MetodoCaixa = meio === 'pix' ? 'pix' : 'cartao';
    const v: VendaRegistrada = {
      ok: true,
      pedido_id: situacao.pedido_id ?? base.pedidoId,
      numero: situacao.numero ?? base.numero,
      total_centavos: situacao.total_centavos ?? base.totalCentavos,
      recebido_centavos: null,
      troco_centavos: 0,
      metodo,
      fichas: situacao.fichas ?? [],
      imprimir: situacao.imprimir,
    };
    setMaquininha(null);
    setPendente(null);
    setQuantidades(new Map());
    setVenda(v);
    setVendaMaquininha(true);
    setImpressao(IMPRESSAO_VAZIA);
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) vibrar(100);
    tocarBipes(1, 880);
    if (situacao.imprimir) void imprimirFichas(v.pedido_id, false);
  }

  function concluirCartao(situacao: SituacaoCaixa) {
    if (!maquininha) return;
    concluirVendaMaquininha(situacao, {
      pedidoId: maquininha.pedidoId,
      numero: maquininha.numero,
      totalCentavos: maquininha.totalCentavos,
    });
  }

  function voltarMaquininha(mensagem: string | null) {
    setMaquininha(null);
    setPendente(null);
    if (mensagem) setRecusa(mensagem);
  }

  // Cancelar uma venda que ficou pendente na lista.
  async function cancelarPendente() {
    if (!pendente || registrando) return;
    const alvo = pendente;
    setRegistrando(true);
    try {
      const { data, error } = await supabase.functions.invoke('caixa-cobrar', {
        body: { acao: 'cancelar', pedido_id: alvo.pedido_id },
      });
      if (error) {
        setRecusa('Sem conexão agora. Tente de novo.');
        return;
      }
      if (data?.ok) {
        setPendente(null);
        return;
      }
      if (data?.na_maquininha) {
        setMaquininha({
          pedidoId: alvo.pedido_id,
          numero: alvo.numero,
          totalCentavos: alvo.total_centavos,
          tipo: null,
        });
        setPendente(null);
        return;
      }
      if (data?.situacao?.status === 'pago') {
        concluirVendaMaquininha(data.situacao as SituacaoCaixa, {
          pedidoId: alvo.pedido_id,
          numero: alvo.numero,
          totalCentavos: alvo.total_centavos,
        });
        return;
      }
      setRecusa(data?.erro ?? 'Não foi possível cancelar. Tente de novo.');
    } catch (e) {
      setRecusa(mensagemAmigavel(e));
    } finally {
      setRegistrando(false);
    }
  }

  function continuarPendente() {
    if (!pendente) return;
    setMaquininha({
      pedidoId: pendente.pedido_id,
      numero: pendente.numero,
      totalCentavos: pendente.total_centavos,
      tipo: null,
    });
    setPendente(null);
  }

  async function imprimirFichas(pedidoId: number, reimpressao: boolean) {
    setImpressao({ imprimindo: true, resultado: null, itens: [], total: 0 });
    try {
      const { data, error } = await supabase.functions.invoke('caixa-imprimir', {
        body: reimpressao
          ? { pedido_id: pedidoId, reimpressao: true }
          : { pedido_id: pedidoId, rapido: true },
      });
      if (error) {
        setImpressao({
          imprimindo: false,
          resultado: { tipo: 'erro', mensagem: 'Sem conexão agora. Tente de novo.' },
          itens: [],
          total: 0,
        });
        return;
      }

      // Resposta rápida: a ficha ficou "a caminho" da maquininha. Libera a tela
      // na hora e continua acompanhando em segundo plano.
      if (data?.a_caminho === true) {
        const totalCaminho = Number(data?.total ?? 0);
        setImpressao({
          imprimindo: false,
          resultado: { tipo: 'a_caminho', total: totalCaminho },
          itens: [],
          total: totalCaminho,
        });
        acompanharImpressao(pedidoId);
        return;
      }

      const totalNum = Number(data?.total ?? 0);
      const impressas = Number(data?.impressas ?? 0);
      const enviadas = Number(data?.enviadas ?? 0);
      const itens = (Array.isArray(data?.resultados) ? data.resultados : []) as ResultadoImpressaoItem[];

      let resultado: ResultadoImpressao;
      if (!data?.ok) {
        resultado = { tipo: 'erro', mensagem: data?.mensagem || data?.erro || 'Não deu para imprimir agora.' };
      } else if (!data?.total) {
        resultado = { tipo: 'nada' };
      } else if (impressas === totalNum) {
        resultado = { tipo: 'ok', total: totalNum };
      } else if (itens.some((i) => i.status === 'erro') || enviadas === 0) {
        resultado = { tipo: 'erro', mensagem: data?.mensagem || 'Alguma ficha não saiu. Toque em Reimprimir.' };
      } else {
        resultado = { tipo: 'aviso', mensagem: data?.mensagem || 'Confira a maquininha e toque em Reimprimir.' };
      }
      setImpressao({ imprimindo: false, resultado, itens, total: totalNum });
    } catch (e) {
      setImpressao({
        imprimindo: false,
        resultado: { tipo: 'erro', mensagem: mensagemAmigavel(e) },
        itens: [],
        total: 0,
      });
    }
  }

  // Acompanha a ficha que ficou "a caminho": a cada 3 s, por até 45 s, pergunta
  // ao banco se a maquininha imprimiu. Não trava a tela nem a próxima venda.
  function acompanharImpressao(pedidoId: number) {
    let tentativas = 0;
    const maxTentativas = 15; // 15 × 3 s = 45 s
    const timer = setInterval(async () => {
      tentativas += 1;
      let situacao: SituacaoPedidoCaixa | null = null;
      try {
        const { data, error } = await supabase.rpc('caixa_situacao_pedido', {
          p_pedido: pedidoId,
        });
        if (error) throw error;
        situacao = (data as SituacaoPedidoCaixa) ?? null;
      } catch {
        /* silencioso: tenta de novo no próximo ciclo */
      }

      const impressoes = (situacao?.impressoes ?? []) as ImpressaoPedido[];

      // Todas impressas: fim com sucesso.
      if (impressoes.length > 0 && impressoes.every((i) => i.status === 'impressa')) {
        parar(timer);
        if (ultimaVendaRef.current?.pedido_id === pedidoId && barraVisivelRef.current) {
          mostrarToast(`${impressoes.length} ficha(s) impressa(s)`, 4000, 'sucesso');
        }
        return;
      }

      // Alguma ficha deu erro: marca a barra vermelha e avisa.
      const comErro = impressoes.find((i) => i.status === 'erro');
      if (comErro) {
        parar(timer);
        setUltimaVenda((atual) =>
          atual && atual.pedido_id === pedidoId ? { ...atual, fichaPendente: true } : atual,
        );
        mostrarToast(
          comErro.erro || 'A ficha não saiu. Toque em Reimprimir.',
          6000,
          'erro',
        );
        return;
      }

      // Passou o tempo sem confirmação: marca pendente e orienta o operador.
      if (tentativas >= maxTentativas) {
        parar(timer);
        setUltimaVenda((atual) =>
          atual && atual.pedido_id === pedidoId ? { ...atual, fichaPendente: true } : atual,
        );
        mostrarToast(
          'A maquininha não confirmou a ficha. Confira o papel; se não saiu, toque em Reimprimir.',
          6000,
          'aviso',
        );
      }
    }, 3000);
    acompanhamentosRef.current.add(timer);
    function parar(t: ReturnType<typeof setInterval>) {
      clearInterval(t);
      acompanhamentosRef.current.delete(t);
    }
  }

  // Fecha o cartão de sucesso e deixa a barra "última venda" no topo.
  const fecharVenda = useCallback(() => {
    if (venda) {
      const fichas = venda.fichas.reduce((s, f) => s + f.quantidade, 0);
      const rImpressao = impressao.resultado;
      const fichaPendente =
        rImpressao != null && (rImpressao.tipo === 'erro' || rImpressao.tipo === 'aviso');
      setUltimaVenda({
        pedido_id: venda.pedido_id,
        numero: venda.numero,
        total_centavos: venda.total_centavos,
        metodoLabel: ROTULO_METODO[venda.metodo] ?? venda.metodo,
        fichas,
        podeDesfazer: !vendaMaquininha,
        fichaPendente,
      });
    }
    setVenda(null);
    setImpressao(IMPRESSAO_VAZIA);
    void carregarCaixa(eventoId ?? 0).catch(() => undefined);
  }, [venda, vendaMaquininha, eventoId, impressao]);

  const comMaquininha = caixaEscolhido?.cartao_na_maquininha === true;
  const imprimeFichasCaixa = caixaEscolhido?.imprime_fichas ?? false;

  const rotuloCaixa = caixaEscolhido
    ? `${caixaEscolhido.nome}${
        caixaEscolhido.terminal_apelido ? ` · ${caixaEscolhido.terminal_apelido}` : ''
      }`
    : '';

  // Não se troca de caixa quando a direção mandou: no modo equipe (caixa da tela)
  // ou quando a direção definiu o caixa desta conta pessoal.
  const caixaTravado =
    (caixaDaDirecao != null && caixas.some((c) => c.caixa === caixaDaDirecao)) ||
    (modoEquipe && telaCaixa?.caixa_id != null && caixas.some((c) => c.caixa === telaCaixa.caixa_id));

  // ---- Estados ----
  if (estado === 'carregando') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100">
        <span className="flex h-12 w-12 items-center justify-center text-primary-500">
          <i className="ri-loader-4-line animate-spin text-4xl" />
        </span>
      </div>
    );
  }

  if (estado === 'erro') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] rounded-2xl border border-secondary-200 bg-white p-8 text-center">
          <h1 className="text-xl font-medium text-foreground-950">Não foi possível carregar</h1>
          <p className="mt-2 text-sm text-foreground-600">{erro}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  if (estado === 'sem-evento') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-10">
        <div className="w-full max-w-[480px] space-y-4">
          <DicaPrimeiraVez id="caixa.sem-evento" />
          {!modoEquipe && (
            <div className="flex justify-center">
              <Botao variante="secundario" icone="ri-arrow-left-line" onClick={() => navigate('/')}>
                Voltar ao início
              </Botao>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (estado === 'escolher-evento') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[480px] px-4 py-10">
          <h1 className="text-center text-xl font-medium text-foreground-950">
            Em qual evento você está no caixa?
          </h1>
          <div className="mt-6 space-y-3">
            {candidatos.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => abrirEvento(c.id)}
                className="flex w-full cursor-pointer items-center justify-between rounded-2xl border border-secondary-200 bg-white px-5 py-4 text-left transition-colors hover:border-primary-300"
              >
                <span className="text-base font-medium text-foreground-950">{c.nome}</span>
                <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                  <i className="ri-arrow-right-s-line text-xl" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!caixa) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100">
        <span className="flex h-12 w-12 items-center justify-center text-primary-500">
          <i className="ri-loader-4-line animate-spin text-4xl" />
        </span>
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background-100">
      {/* Topo */}
      <header className="shrink-0 border-b border-secondary-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="min-w-0 truncate text-base font-semibold text-foreground-950">
                {caixa.evento.nome}
              </h1>
              <Dica id="caixa" />
            </div>
            <p className="text-sm text-secondary-500">
              Caixa · {caixa.operador} · <span className="tabular-nums">{relogio}</span>
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {caixa.direcao && !modoEquipe && (
              <button
                type="button"
                onClick={() => navigate(`/gerenciar/${caixa.evento.id}/loja`)}
                className="cursor-pointer whitespace-nowrap rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                ← Gerenciar
              </button>
            )}
            <button
              type="button"
              onClick={() => setRecentes(true)}
              aria-label="Últimas vendas"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-secondary-200 bg-white text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-history-line text-lg" />
            </button>
            <button
              type="button"
              onClick={() => navigate(`/caixa/fechamento?evento=${caixa.evento.id}`)}
              className="cursor-pointer whitespace-nowrap rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
            >
              Fechar caixa
            </button>
          </div>
        </div>

        {/* Chip do caixa escolhido + aviso da maquininha */}
        {caixaEscolhido && (
          <div className="mt-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {caixas.length > 1 && !caixaTravado ? (
                <button
                  type="button"
                  onClick={() => setEscolhendoCaixa(true)}
                  aria-label="Trocar de caixa"
                  className="cursor-pointer"
                >
                  <Chip tipo="info">
                    {rotuloCaixa}
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-arrow-down-s-line" />
                    </span>
                  </Chip>
                </button>
              ) : (
                <Chip tipo="info">{rotuloCaixa}</Chip>
              )}
              {comMaquininha && (
                <p className="flex items-center gap-1.5 text-xs font-medium text-secondary-500">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-printer-line" />
                  </span>
                  Deixe a maquininha na tela Vendas vinculadas.
                </p>
              )}
            </div>
            {caixaDaDirecao != null && !modoEquipe && (
              <p className="mt-1 text-xs text-secondary-500">Caixa definido pela direção</p>
            )}
          </div>
        )}
      </header>

      {/* Total da venda: gigante, no topo, sempre visível */}
      {total > 0 && (
        <div className="shrink-0 bg-primary-500 px-4 py-2.5 text-white">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-white/80">
              Total
            </span>
            <span className="text-[40px] font-extrabold leading-none tabular-nums">
              <NumeroAnimado valor={formatarCentavos(total)} />
            </span>
          </div>
        </div>
      )}

      {turno && <FaixaCaixaAberto turno={turno} aoSangria={() => setSangriaAberta(true)} />}

      {/* Avisos: venda pendente e barra da última venda */}
      {(pendente || (ultimaVenda && barraVisivel)) && (
        <div className="shrink-0 space-y-2 px-4 pt-3">
          {pendente && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-warning-200 bg-warning-50 px-3 py-2.5">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-warning-600">
                <i className="ri-time-line" />
              </span>
              <span className="min-w-0 flex-1 text-sm font-medium text-warning-900">
                Venda #{pendente.numero} · {formatarCentavos(pendente.total_centavos)} aguardando a
                maquininha
              </span>
              <button
                type="button"
                disabled={registrando}
                onClick={continuarPendente}
                className="cursor-pointer whitespace-nowrap rounded-lg bg-warning-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-warning-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Continuar
              </button>
              <button
                type="button"
                disabled={registrando}
                onClick={() => void cancelarPendente()}
                className="cursor-pointer whitespace-nowrap rounded-lg border border-warning-300 bg-white px-3 py-1.5 text-sm font-medium text-warning-800 transition-colors hover:bg-warning-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancelar
              </button>
            </div>
          )}
          {ultimaVenda && barraVisivel && (
            <BarraUltimaVenda
              venda={ultimaVenda}
              imprime={imprimeFichasCaixa}
              direcao={caixa.direcao}
              aoReimprimir={() => setBarraReimprimir(ultimaVenda.pedido_id)}
              aoDesfazer={() => setBarraDesfazer(ultimaVenda.pedido_id)}
              aoFechar={() => setBarraVisivel(false)}
            />
          )}
        </div>
      )}

      {/* Corpo: grade à esquerda, pedido fixo embaixo (celular) ou à direita (tablet). */}
      <div className="flex flex-1 flex-col lg:flex-row">
        <div className="flex-1 overflow-y-auto px-4 py-4 pb-[74vh] lg:pb-4">
          {turnoOk && !turno && (caixaEscolhido || caixas.length === 0) && !turnoDispensado && (
            <CartaoAbrirCaixa
              aoAbrir={abrirCaixaTurno}
              aoDispensar={() => setTurnoDispensado(true)}
            />
          )}
          {caixa.pontos.length === 0 ? (
            <div className="rounded-2xl border border-secondary-200 bg-white p-8 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-store-2-line text-2xl" />
              </span>
              <h2 className="mt-4 text-lg font-medium text-foreground-950">
                Nenhuma {caixa.termo.singular} disponível
              </h2>
              <p className="mt-1 text-sm text-foreground-600">
                As fichas ainda não foram montadas para este evento.
              </p>
            </div>
          ) : (
            <GradeProdutos
              pontos={caixa.pontos}
              quantidades={quantidades}
              aoAdicionar={aoAdicionar}
              aoRemover={aoRemover}
              aoEditar={(p) => setQuantidadeAlvo(p)}
            />
          )}
        </div>

        <aside className="fixed inset-x-0 bottom-0 z-30 max-h-[70vh] overflow-y-auto rounded-t-2xl border-t border-secondary-200 bg-white p-4 lg:sticky lg:top-0 lg:z-auto lg:max-h-[100dvh] lg:w-[380px] lg:shrink-0 lg:self-start lg:rounded-none lg:border-l lg:border-t-0 lg:py-4">
          <PainelPedido
            comMaquininha={comMaquininha}
            evento={caixa.evento}
            linhas={linhas}
            total={total}
            registrando={registrando}
            aoLimpar={aoLimpar}
            aoPagar={aoPagar}
            aoAdicionarLinha={aoAdicionar}
            aoRemoverLinha={aoRemover}
          />
        </aside>
      </div>

      {folhaDinheiro && (
        <FolhaDinheiro
          total={total}
          confirmando={registrando}
          aoConfirmar={(recebido) => registrarVenda('dinheiro', recebido)}
          aoFechar={() => setFolhaDinheiro(false)}
        />
      )}

      {quantidadeAlvo && (
        <FolhaQuantidade
          produto={quantidadeAlvo}
          atual={quantidades.get(quantidadeAlvo.produto_id) ?? 0}
          aoConfirmar={(qtd) => {
            definirQuantidade(quantidadeAlvo.produto_id, qtd);
            setQuantidadeAlvo(null);
          }}
          aoFechar={() => setQuantidadeAlvo(null)}
        />
      )}

      {confirmandoMetodo != null && (
        <ModalConfirmacao
          metodo={confirmandoMetodo}
          total={total}
          registrando={registrando}
          aoConfirmar={() => registrarVenda(confirmandoMetodo, null)}
          aoFechar={() => setConfirmandoMetodo(null)}
        />
      )}

      {maquininha && (
        <TelaCartao
          numero={maquininha.numero}
          totalCentavos={maquininha.totalCentavos}
          pedidoId={maquininha.pedidoId}
          tipo={maquininha.tipo}
          aoPago={concluirCartao}
          aoVoltar={voltarMaquininha}
        />
      )}

      {venda && (
        <TelaSucesso
          venda={venda}
          impressao={impressao}
          imprimeCaixa={!!venda.imprimir}
          aoNovaVenda={fecharVenda}
          aoReimprimir={() => void imprimirFichas(venda.pedido_id, true)}
        />
      )}

      {recusa && <TelaRecusa mensagem={recusa} aoVoltar={() => setRecusa(null)} />}

      {sangriaAberta && turno && (
        <ModalSangria
          turno={turno}
          aoFechar={() => setSangriaAberta(false)}
          aoRegistrar={(novo) => setTurno(novo)}
        />
      )}

      {recentes && eventoId != null && (
        <ModalRecentes
          eventoId={eventoId}
          direcao={caixa.direcao}
          imprime={imprimeFichasCaixa}
          aoFechar={() => setRecentes(false)}
          aoDesfeito={() => carregarCaixa(eventoId)}
        />
      )}

      {/* Reimprimir/Desfazer pela barra "última venda" */}
      <FolhaReimprimir
        aberto={barraReimprimir != null}
        pedidoId={barraReimprimir}
        aoFechar={() => setBarraReimprimir(null)}
        aoFim={(mensagem, deuCerto) => {
          if (deuCerto) {
            setUltimaVenda((atual) => (atual ? { ...atual, fichaPendente: false } : atual));
            mostrarToast(mensagem, 4000, 'sucesso');
          } else {
            mostrarToast(mensagem, 4000, 'erro');
          }
        }}
      />
      <ModalDesfazer
        aberto={barraDesfazer != null}
        pedidoId={barraDesfazer}
        direcao={caixa.direcao}
        aoFechar={() => setBarraDesfazer(null)}
        aoDesfeito={(mensagem) => {
          mostrarToast(mensagem, 4000, 'sucesso');
          setUltimaVenda(null);
          void carregarCaixa(eventoId ?? 0);
        }}
      />

      <FolhaEscolherCaixa
        aberto={escolhendoCaixa}
        caixas={caixas}
        escolhido={caixaEscolhido?.caixa ?? null}
        obrigatoria={caixaEscolhido == null}
        aoEscolher={escolherCaixa}
        aoFechar={() => setEscolhendoCaixa(false)}
      />
    </div>
  );
}

// Confirmação rápida para Pix/Cartão "por fora" (caixa SEM maquininha).
function ModalConfirmacao({
  metodo,
  total,
  registrando,
  aoConfirmar,
  aoFechar,
}: {
  metodo: MetodoCaixa;
  total: number;
  registrando: boolean;
  aoConfirmar: () => void;
  aoFechar: () => void;
}) {
  const rotulo = metodo === 'pix' ? 'Pix' : 'Cartão';
  const icone = metodo === 'pix' ? 'ri-qr-code-line' : 'ri-bank-card-line';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-[400px] rounded-3xl bg-white p-6 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <i className={`${icone} text-2xl`} />
        </span>
        <h2 className="mt-4 text-xl font-bold text-foreground-950">
          Recebeu {formatarCentavos(total)} no {rotulo}?
        </h2>
        <p className="mt-1 text-sm text-secondary-500">Só registra a venda — não cobra nada por aqui.</p>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={aoFechar}
            disabled={registrando}
            className="cursor-pointer whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-6 py-3 text-base font-semibold text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Ainda não
          </button>
          <button
            type="button"
            onClick={aoConfirmar}
            disabled={registrando}
            className="cursor-pointer whitespace-nowrap rounded-2xl bg-success-600 px-6 py-3 text-base font-bold text-white transition-colors hover:bg-success-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {registrando ? 'Registrando…' : 'Sim, recebi'}
          </button>
        </div>
      </div>
    </div>
  );
}